import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useId, useRef } from 'react'

import type { MutableRefObject, ReactNode } from 'react'

type DialogProps = {
    open: boolean
    title: string
    onClose: () => void
    children: ReactNode
    footer?: ReactNode
    initialFocusRef?: MutableRefObject<HTMLElement | null>
    closeLabel: string
    dismissible?: boolean
}

export const Dialog = ({
    open,
    title,
    onClose,
    children,
    footer,
    initialFocusRef,
    closeLabel,
    dismissible = true,
}: DialogProps) => {
    const titleId = useId()
    const panelRef = useRef<HTMLDivElement | null>(null)
    const closeButtonRef = useRef<HTMLButtonElement | null>(null)
    const previousFocusRef = useRef<HTMLElement | null>(null)
    const onCloseRef = useRef(onClose)
    const dismissibleRef = useRef(dismissible)
    dismissibleRef.current = dismissible

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (!open) {
            return
        }

        previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const timeout = window.setTimeout(() => {
            const focusTarget = initialFocusRef?.current ?? closeButtonRef.current ?? panelRef.current
            focusTarget?.focus()
        }, 0)

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()

                if (dismissibleRef.current) {
                    onCloseRef.current()
                }
            }

            if (event.key === 'Tab') {
                const panel = panelRef.current
                const focusable = panel?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
                )
                const first = focusable?.[0]
                const last = focusable?.[focusable.length - 1]

                if (!first || !last) {
                    event.preventDefault()
                    panel?.focus()
                } else if (
                    event.shiftKey &&
                    (document.activeElement === first || !panel?.contains(document.activeElement))
                ) {
                    event.preventDefault()
                    last.focus()
                } else if (
                    !event.shiftKey &&
                    (document.activeElement === last || !panel?.contains(document.activeElement))
                ) {
                    event.preventDefault()
                    first.focus()
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKeyDown)
            window.clearTimeout(timeout)
            previousFocusRef.current?.focus()
        }
    }, [initialFocusRef, open])

    if (!open) {
        return null
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
            onClick={(event) => {
                if (dismissible && event.target === event.currentTarget) {
                    onClose()
                }
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className="bg-account-surface flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-lg shadow-2xl outline-none"
            >
                <div className="border-account-divider flex shrink-0 items-start justify-between gap-3 border-b px-4 py-4 sm:px-6">
                    <h3 id={titleId} className="text-account-strong min-w-0 text-lg font-bold">
                        {title}
                    </h3>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        aria-label={closeLabel}
                        disabled={!dismissible}
                        onClick={onClose}
                        className="text-account-faint hover:text-account-secondary shrink-0 p-1 transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="min-h-0 space-y-6 overflow-y-auto p-4 sm:p-6">{children}</div>

                {footer && (
                    <div className="border-account-divider bg-account-muted shrink-0 border-t px-4 py-4 sm:px-6">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    )
}
